import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Popup from "../workshop/popup";
import { Symbol } from "../workshop/starPicker";

const StatsChart = ({ data, halfCol = false, showSelectors = false }) => {
  const { i18n, t } = useTranslation();
  const [sortedData, setSortedData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (data) {
      const sortedData = data.sort((a, b) => {
        const totalAValue = (a?.value || 0) + (a?.value2 || 0);
        const totalBValue = (b?.value || 0) + (b?.value2 || 0);

        if (totalBValue === totalAValue) {
          return a.text.localeCompare(b.text, i18n.language);
        }
        return totalBValue - totalAValue;
      });
      setSortedData(sortedData);
    }
  }, [data, i18n.language]);

  return (
    <div className="w-full">
      {sortedData.map((item, index) => {
        const highestValue = (sortedData?.[0]?.value || 0) + (sortedData?.[0]?.value2 || 0);
        const currentValue = (item.value || 0) + (item?.value2 || 0);

        const percentage = ((currentValue / highestValue) * 100).toFixed(2);

        const partialAPercentage = item?.value ? ((item.value / currentValue) * 100).toFixed(2) : 0;
        const partialBPercentage = item?.value2
          ? ((item.value2 / currentValue) * 100).toFixed(2)
          : 0;

        return (
          <div key={index} className="grid grid-cols-12 gap-1 my-2">
            <div
              className={`${!halfCol ? "col-span-12 sm:col-span-3" : "col-span-12 sm:col-span-6"} text-gray-250 text-base font-medium"`}
              dangerouslySetInnerHTML={{ __html: item.text }}
            />
            <div
              className={`${!halfCol ? "col-span-12 sm:col-span-9" : "col-span-12 sm:col-span-6 sm:pl-2"} pr-6`}>
              <span
                className="flex"
                style={{
                  width: `${percentage}%`
                }}
                onClick={() => showSelectors && setSelectedItem({ item })}>
                {partialAPercentage > 0 && (
                  <span
                    className={`flex items-center justify-end pr-2 py-1/2 text-white-100 font-medium text-sm rounded ${showSelectors ? "cursor-pointer" : ""}`}
                    style={{
                      width: `${partialAPercentage}%`,
                      backgroundColor: item?.color || "#E59536",
                      color: item?.textColor || "#FFF",
                      borderRadius: partialBPercentage > 0 ? "0.25rem 0 0 0.25rem" : "0.25rem"
                    }}>
                    {item.value}
                  </span>
                )}
                {partialBPercentage > 0 && (
                  <span
                    className={`flex items-center justify-end pr-2 py-1/2 text-white-100 font-medium text-sm rounded ${showSelectors ? "cursor-pointer" : ""}`}
                    style={{
                      width: `${partialBPercentage}%`,
                      backgroundColor: item?.color2 || "#E59536",
                      color: item?.textColor2 || "#FFF",
                      borderRadius: partialAPercentage > 0 ? "0 0.25rem 0.25rem 0" : "0.25rem"
                    }}>
                    {item.value2}
                  </span>
                )}
              </span>
            </div>
          </div>
        );
      })}

      <Popup
        open={!!selectedItem}
        setOpen={() => setSelectedItem(null)}
        maxWidth="max-w-md"
        containerStyle="!bg-orange-50"
        autoHeight={true}
        mobilePopupStyle={false}
        bottomPadding={false}>
        {selectedItem && (
          <div className="p-4 flex flex-col gap-1 justify-center items-center">
            <h3
              className="text-xl text-orange-100 font-semibold mb-4"
              dangerouslySetInnerHTML={{ __html: t("statsChart.popup.title") }}
            />
            <p
              className="text-sm text-gray-300 font-semibold mb-4 text-center"
              dangerouslySetInnerHTML={{ __html: selectedItem.item.text }}
            />
            <div className="flex items-center gap-2">
              {selectedItem.item.selectorNames?.map((name) => (
                <Symbol key={`a-${name}`} name={name} type="a" filled={true} />
              ))}
              {selectedItem.item.selectorNames2?.map((name) => (
                <Symbol key={`b-${name}`} name={name} type="b" filled={true} />
              ))}
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
};

export default StatsChart;
